import { useCallback, useEffect, useRef, useState } from 'react';
import type { GeocodeSuggestion } from '@pms/shared';
import { geocodeAutocomplete } from '../services/api/geocode.api';

export type LocationStatus = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

interface Options {
  limit?: number;
  minChars?: number;
  debounceMs?: number;
}

interface LocationAutocomplete {
  query: string;
  setQuery: (q: string) => void;
  results: GeocodeSuggestion[];
  status: LocationStatus;
  reset: () => void;
}

/**
 * Debounced, abortable geocode autocomplete (TASK-146). Min 3 chars, 300ms
 * debounce, one in-flight request (aborts the prior). A graceful empty from the
 * proxy maps to 'empty'; only a real network/abort-independent failure is 'error'.
 * Duplicated per app (see apps/web) — shared types only, per design.
 */
export function useLocationAutocomplete(opts: Options = {}): LocationAutocomplete {
  const { limit = 6, minChars = 2, debounceMs = 250 } = opts;

  const [query, setQueryState] = useState('');
  const [results, setResults] = useState<GeocodeSuggestion[]>([]);
  const [status, setStatus] = useState<LocationStatus>('idle');

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  const abortInFlight = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  };

  const reset = useCallback(() => {
    clearTimer();
    abortInFlight();
    setResults([]);
    setStatus('idle');
  }, []);

  const setQuery = useCallback(
    (q: string) => {
      setQueryState(q);
      clearTimer();

      if (q.trim().length < minChars) {
        abortInFlight();
        setResults([]);
        setStatus('idle');
        return;
      }

      // Open the dropdown immediately with a loading state so suggestions feel
      // instant as the user types (the actual fetch stays debounced below).
      setStatus('loading');

      timerRef.current = setTimeout(() => {
        abortInFlight();
        const controller = new AbortController();
        abortRef.current = controller;
        setStatus('loading');

        geocodeAutocomplete(q.trim(), limit, controller.signal)
          .then((res) => {
            if (controller.signal.aborted) return;
            setResults(res.results);
            setStatus(res.results.length ? 'ready' : 'empty');
          })
          .catch((err: unknown) => {
            // Aborted (new keystroke / unmount) — not a real error.
            if (controller.signal.aborted) return;
            if (err instanceof Error && err.name === 'CanceledError') return;
            setResults([]);
            setStatus('error');
          });
      }, debounceMs);
    },
    [limit, minChars, debounceMs],
  );

  // Abort any in-flight request + clear timer on unmount.
  useEffect(() => () => reset(), [reset]);

  return { query, setQuery, results, status, reset };
}
